import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { setDiagnostics } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';

import { TemplateValidationError } from '@/types';
import { jinjaLanguage } from './jinja-language';

export interface CodeMirrorTemplateHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

interface CodeMirrorTemplateProps {
  value: string;
  onChange: (next: string) => void;
  errors?: TemplateValidationError[];
  readOnly?: boolean;
  minHeight?: string;
}

export const CodeMirrorTemplate = forwardRef<CodeMirrorTemplateHandle, CodeMirrorTemplateProps>(
  ({ value, onChange, errors = [], readOnly = false, minHeight = '500px' }, ref) => {
    const cmRef = useRef<ReactCodeMirrorRef>(null);

    useImperativeHandle(ref, () => ({
      insertAtCursor: (text: string) => {
        const view = cmRef.current?.view;
        if (!view) {
          return;
        }
        const { from, to } = view.state.selection.main;
        view.dispatch({
          changes: { from, to, insert: text },
          selection: { anchor: from + text.length },
        });
        view.focus();
      },
      focus: () => cmRef.current?.view?.focus(),
    }));

    // Push validation errors as CodeMirror lint diagnostics. The parent owns
    // the validate cycle and just hands us the latest errors; we translate.
    useEffect(() => {
      const view = cmRef.current?.view;
      if (!view) {
        return;
      }
      const docLines = view.state.doc.lines;
      const diagnostics = errors
        .filter((e) => e.line != null && e.line >= 1 && e.line <= docLines)
        .map((e) => {
          const line = view.state.doc.line(e.line as number);
          return {
            from: line.from,
            to: line.to,
            severity: 'error' as const,
            message: e.message,
          };
        });
      view.dispatch(setDiagnostics(view.state, diagnostics));
    }, [errors]);

    const extensions = useMemo(() => [jinjaLanguage, EditorView.lineWrapping], []);

    return (
      <CodeMirror
        ref={cmRef}
        value={value}
        onChange={onChange}
        extensions={extensions}
        editable={!readOnly}
        readOnly={readOnly}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLine: !readOnly,
          autocompletion: false,
        }}
        minHeight={minHeight}
        style={{ fontSize: 13 }}
      />
    );
  }
);
